import React, { useEffect, useState } from 'react';
import ProductionSheetHeader from '../production-sheet/components/ProductionSheetHeader';
import ProductionSheetBody, {
  emptyProductionForm,
  formFromBatch,
} from '../production-sheet/components/ProductionSheetBody';
import { supabase } from '../../lib/supabase';
import {
  CASE_SHEET_SELECT,
  mapCaseToSheetPatientData,
} from '../../lib/caseSheetPatientData';
import { fetchConceptionPhaseTotalAligners } from '../../services/caseProductionPhasesService';

/**
 * Aperçu atelier de la fiche production (QR + patient/cas + champs lot).
 */
export default function WorkshopProductionSheetContent({ batch, caseId }) {
  const [patientData, setPatientData] = useState(null);
  const [conceptionAligners, setConceptionAligners] = useState(null);
  const [loadingPatient, setLoadingPatient] = useState(Boolean(caseId));

  useEffect(() => {
    if (!caseId) {
      setPatientData(null);
      setConceptionAligners(null);
      setLoadingPatient(false);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoadingPatient(true);
      try {
        const [caseResult, aligners] = await Promise.all([
          supabase.from('cases').select(CASE_SHEET_SELECT).eq('id', caseId).single(),
          fetchConceptionPhaseTotalAligners(caseId),
        ]);
        if (caseResult.error) throw caseResult.error;
        if (!cancelled) {
          setPatientData(mapCaseToSheetPatientData(caseResult.data));
          setConceptionAligners(aligners);
        }
      } catch (err) {
        console.error('[WorkshopProductionSheetContent] case load:', err);
        if (!cancelled) {
          setPatientData({
            patientName: batch?.patientName || batch?.patient_name || '—',
            patientId: '—',
            caseNumber: '—',
            dateOfBirth: '—',
            dentistName: '—',
            clinicName: batch?.clinicName || batch?.clinic_name || '—',
            createdDate: '—',
            treatmentType: 'Aligneurs',
            caseId,
          });
          setConceptionAligners(null);
        }
      } finally {
        if (!cancelled) setLoadingPatient(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [caseId, batch?.patientName, batch?.patient_name, batch?.clinicName, batch?.clinic_name]);

  if (!batch) {
    return (
      <p className="text-sm text-muted-foreground text-center py-12">Aucun lot chargé.</p>
    );
  }

  if (loadingPatient) {
    return (
      <div className="flex flex-col items-center gap-3 py-16">
        <div className="h-8 w-8 rounded-full border-2 border-primary border-t-transparent animate-spin" />
        <p className="text-sm text-muted-foreground">Chargement de la fiche…</p>
      </div>
    );
  }

  const form = formFromBatch(batch, conceptionAligners ?? '');
  const displayBatch = {
    ...batch,
    model_up_from: form.model_up_from,
    model_up_to: form.model_up_to,
    model_low_from: form.model_low_from,
    model_low_to: form.model_low_to,
  };
  const headerPatient =
    patientData ||
    ({
      patientName: batch.patientName || batch.patient_name || '—',
      patientId: '—',
      caseNumber: '—',
      dateOfBirth: '—',
      dentistName: '—',
      clinicName: batch.clinicName || batch.clinic_name || '—',
      createdDate: '—',
      treatmentType: 'Aligneurs',
      caseId,
    });

  return (
    <div className="ipr-sheet-shell overflow-hidden bg-white">
      <ProductionSheetHeader patientData={headerPatient} batch={displayBatch} />
      <ProductionSheetBody form={form || emptyProductionForm()} setField={() => () => {}} readOnly />
      <p className="text-xs text-muted-foreground text-center px-4 pb-4 leading-relaxed">
        Lecture seule — utilisez « Modifier la fiche » pour éditer.
      </p>
    </div>
  );
}
